// src/components/customers/CalendarEventModal.jsx
import { useEffect, useRef } from "react";

/**
 * CalendarEventModal Component
 *
 * A modal dialog for creating calendar events (appointments) for customer jobs.
 * Supports different event types: measurement, installation, price, follow-up, quote, reschedule, pickup, delivery.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} props.calendarParams - Calendar event parameters (title, start, end, description)
 * @param {Function} props.onCalendarParamChange - Function to handle calendar input changes
 * @param {string} props.eventType - Selected event type ('measurement', 'installation', 'price')
 * @param {Function} props.onEventTypeChange - Function to handle event type changes
 * @param {Function} props.onSave - Function to save the calendar event
 */
const CalendarEventModal = ({
  isOpen,
  onClose,
  calendarParams,
  onCalendarParamChange,
  eventType,
  onEventTypeChange,
  onSave,
}) => {
  const titleInputRef = useRef();

  // Set default dates when modal opens for new events
  useEffect(() => {
    if (isOpen && (!calendarParams.start || !calendarParams.end)) {
      const now = new Date();
      const startTime = new Date(now);
      const endTime = new Date(now);

      // Round to next hour for cleaner default
      startTime.setMinutes(0, 0, 0);
      startTime.setHours(startTime.getHours() + 1);

      // End time is 1 hour after start time
      endTime.setTime(startTime.getTime() + 60 * 60 * 1000);

      // Format for datetime-local input
      const formatForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      // Update start time if not set
      if (!calendarParams.start) {
        onCalendarParamChange({
          target: {
            id: "start",
            value: formatForInput(startTime),
          },
        });
      }

      // Update end time if not set
      if (!calendarParams.end) {
        onCalendarParamChange({
          target: {
            id: "end",
            value: formatForInput(endTime),
          },
        });
      }
    }
  }, [isOpen, calendarParams.start, calendarParams.end, onCalendarParamChange]);

  // Focus on first input when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!eventType) {
      alert("Please select an event type");
      return;
    }

    if (!calendarParams.start || !calendarParams.end) {
      alert("Please select start and end dates");
      return;
    }

    // Validate that end date is after start date
    if (new Date(calendarParams.end) <= new Date(calendarParams.start)) {
      alert("End date must be after start date");
      return;
    }

    onSave();
  };

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden"; // Prevent background scroll
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Handle input changes - unified handler with smart date adjustment
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    onCalendarParamChange({
      target: {
        id,
        value,
      },
    });
  };

  /**
   * Handle start date change and auto-adjust end date
   */
  const handleStartDateChange = (e) => {
    const startDate = new Date(e.target.value);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour

    // Format end date for datetime-local input
    const formatForInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Update start date
    handleInputChange(e);

    // Auto-update end date to be 1 hour later
    onCalendarParamChange({
      target: {
        id: "end",
        value: formatForInput(endDate),
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="calendar-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xs sm:max-w-xl md:max-w-2xl lg:max-w-3xl transform transition-all duration-300">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
            <h3
              id="calendar-modal-title"
              className="text-base sm:text-lg font-semibold text-gray-900 flex items-center"
            >
              {/* Calendar icon */}
              <svg
                className="w-6 h-6 mr-2 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Create Calendar Event
            </h3>

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6">
            {/* Event Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Event Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                {/* Measurement */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "measurement"
                      ? "bg-blue-700 border-blue-800 ring-4 ring-blue-300 shadow-lg text-white"
                      : "bg-blue-500 border-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="measurement"
                    checked={eventType === "measurement"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Measurement</span>
                </label>

                {/* Installation */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "installation"
                      ? "bg-green-700 border-green-800 ring-4 ring-green-300 shadow-lg text-white"
                      : "bg-green-500 border-green-500 hover:bg-green-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="installation"
                    checked={eventType === "installation"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Installation</span>
                </label>

                {/* Pricing — note the value is "price" (singular) to match
                    the calendarService validator allowlist + existing data.
                    The UI label stays "Pricing" — only the input value
                    changes. Bug fix 2026-05-16. */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "price"
                      ? "bg-purple-700 border-purple-800 ring-4 ring-purple-300 shadow-lg text-white"
                      : "bg-purple-500 border-purple-500 hover:bg-purple-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="price"
                    checked={eventType === "price"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Pricing</span>
                </label>

                {/* Follow-up */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "follow-up"
                      ? "bg-cyan-700 border-cyan-800 ring-4 ring-cyan-300 shadow-lg text-white"
                      : "bg-cyan-500 border-cyan-500 hover:bg-cyan-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="follow-up"
                    checked={eventType === "follow-up"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Follow-up</span>
                </label>

                {/* Quote */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "quote"
                      ? "bg-slate-700 border-slate-800 ring-4 ring-slate-300 shadow-lg text-white"
                      : "bg-slate-500 border-slate-500 hover:bg-slate-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="quote"
                    checked={eventType === "quote"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Quote</span>
                </label>

                {/* Reschedule */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "reschedule"
                      ? "bg-orange-700 border-orange-800 ring-4 ring-orange-300 shadow-lg text-white"
                      : "bg-orange-500 border-orange-500 hover:bg-orange-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="reschedule"
                    checked={eventType === "reschedule"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Reschedule</span>
                </label>

                {/* Pickup */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "pickup"
                      ? "bg-teal-700 border-teal-800 ring-4 ring-teal-300 shadow-lg text-white"
                      : "bg-teal-500 border-teal-500 hover:bg-teal-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="pickup"
                    checked={eventType === "pickup"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Pickup</span>
                </label>

                {/* Delivery */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "delivery"
                      ? "bg-red-700 border-red-800 ring-4 ring-red-300 shadow-lg text-white"
                      : "bg-red-500 border-red-500 hover:bg-red-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="delivery"
                    checked={eventType === "delivery"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Delivery</span>
                </label>

                {/* Payment */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "payment"
                      ? "bg-emerald-700 border-emerald-800 ring-4 ring-emerald-300 shadow-lg text-white"
                      : "bg-emerald-500 border-emerald-500 hover:bg-emerald-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="payment"
                    checked={eventType === "payment"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2zm12 8h.01" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Payment</span>
                </label>

                {/* Repair */}
                <label
                  className={`flex flex-col items-center justify-center p-2 sm:p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                    eventType === "repair"
                      ? "bg-amber-700 border-amber-800 ring-4 ring-amber-300 shadow-lg text-white"
                      : "bg-amber-500 border-amber-500 hover:bg-amber-600 text-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="eventType"
                    value="repair"
                    checked={eventType === "repair"}
                    onChange={(e) => onEventTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Repair</span>
                </label>
              </div>
              {!eventType && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Please select an event type
                </p>
              )}
            </div>

            {/* Event Title */}
            <div className="mb-4">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Event Title
              </label>
              <input
                ref={titleInputRef}
                type="text"
                id="title"
                value={calendarParams.title || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm sm:text-base"
                placeholder="Enter event title (optional)"
              />
            </div>

            {/* Event Location — prefilled from job address by the parent component
                (handleAddToCalendar in ViewCustomerRooms). Editable in case the
                appointment is at a different address than the job site. */}
            <div className="mb-4">
              <label
                htmlFor="location"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Location
                <span className="text-gray-400 text-xs font-normal ml-2">
                  (prefilled from job address — edit if needed)
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  id="location"
                  value={calendarParams.location || ""}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm sm:text-base"
                  placeholder="Event location (e.g., job site address)"
                />
              </div>
            </div>

            {/* Date and Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Start Date/Time */}
              <div>
                <label
                  htmlFor="start"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Start Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="start"
                  value={calendarParams.start || ""}
                  onChange={handleStartDateChange}
                  className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm sm:text-base"
                  required
                />
              </div>

              {/* End Date/Time */}
              <div>
                <label
                  htmlFor="end"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  End Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="end"
                  value={calendarParams.end || ""}
                  onChange={handleInputChange}
                  min={calendarParams.start || ""}
                  className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 text-sm sm:text-base"
                  required
                />
              </div>
            </div>

            {/* Description Field */}
            <div className="mb-6">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Event Description
              </label>
              <textarea
                id="description"
                value={calendarParams.description || ""}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 resize-y text-sm sm:text-base"
                placeholder="Add any specific details about this appointment..."
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional: Add special instructions, customer preferences, or
                other notes
              </p>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              {/* Cancel Button */}
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-medium text-sm sm:text-base"
              >
                Cancel
              </button>

              {/* Save Button */}
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center font-medium text-sm sm:text-base"
              >
                {/* Calendar plus icon */}
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Create Event
              </button>
            </div>
          </form>

          {/* Helper Text */}
          <div className="px-6 pb-6 space-y-3">{/* Event Types Info */}</div>
        </div>
      </div>
    </div>
  );
};

export default CalendarEventModal;
